/**
 * Pure helpers for tracking-link performance (an asset stats sheet `components/AssetStats`, a link panel's stats
 * line, the video library's badges).
 *
 * A link's stats come from the SAME data-aggregation API the Stats/Home/Campaign pages use: the Owners Hub Stats
 * service's `daily_affiliate_activity` data set, aggregated on the `campaign` dimension and kept to the links' own
 * `campaign` values (spark-creatives mints one per link, see `tracking-links-api.ts`; signup stores it verbatim). An
 * asset can own several links (one per version); this module matches the groups and turns the summed data_points
 * into display figures.
 *
 * Money fields are scaled integers divided by the project-wide {@link AMOUNT_SCALE} (the same divisor Home /
 * Stats / Campaign detail use). `registrations` / `ftds` arrive as STRINGS, so every read goes through
 * `toNumber`. Kept React-free so the matching + aggregation is unit-testable without rendering or network.
 */
import type { AffiliateCampaign } from '@/nova/pages/affiliates/types';
import { AMOUNT_SCALE } from '@/nova/pages/home/stats-mapping';
import { groupDimension, groupMetrics, toNumber, type Metrics } from '@/nova/pages/home/stats-shape';

/**
 * LEGACY (affiliates-platform campaigns, read by the Affiliates page): the values the Stats `campaign` dimension
 * carries for those campaigns, to match a group by.
 *
 * The `campaign` dimension is the campaign ID (confirmed with the backend team). We match by the campaign's
 * `id` (and its numeric `campaign_no` as a hedge, since both are unique per campaign); we deliberately do NOT
 * match by name, so same-named ads (e.g. the default "Untitled video ad") never cross-attribute, and the
 * organic "" / "direct" / "organic" buckets can never be in this set. A non-match reads 0.
 */
export const statMatchKeys = (adCampaigns: readonly AffiliateCampaign[]): Set<string> => {
  const keys = new Set<string>();
  const add = (v: unknown): void => {
    const s = typeof v === 'string' ? v.trim() : v != null ? String(v) : '';
    if (s) keys.add(s);
  };
  for (const c of adCampaigns) {
    add(c.id);
    add(c.campaign_no);
  }
  return keys;
};

/** Normalise a `data-set` payload to its list of dimension groups (a bare array, a `{ data: [...] }`
 *  envelope, or a single group object). A no-filter `aggregate_on:['campaign']` call returns the array form. */
export const campaignGroups = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  const env = (raw as { data?: unknown } | undefined)?.data;
  if (Array.isArray(env)) return env;
  return raw ? [raw] : [];
};

/** The metric object of every group whose echoed `campaign` value matches one of this ad's campaigns. */
export const matchedCampaignMetrics = (groups: readonly unknown[], matchKeys: ReadonlySet<string>): Metrics[] => {
  const out: Metrics[] = [];
  for (const g of groups) {
    const dim = groupDimension(g, 'campaign');
    if (dim !== undefined && matchKeys.has(dim)) out.push(groupMetrics(g));
  }
  return out;
};

/** The metric field(s) each KPI reads from the data_points, and whether it is money (scaled) or a count. */
export interface VideoStatDef {
  /** Stable id / i18n label. */
  id: string;
  /** data_points field(s) to sum for this KPI (money KPIs may sum several, e.g. GGR = casino + sport). */
  fields: readonly string[];
  kind: 'count' | 'money';
}

/**
 * The KPIs the modal renders, in display order. `daily_affiliate_activity` has NO clicks metric (clicks live
 * only in the affiliate `/reports` + Casino Ad Service), so this is the acquisition + value picture the stats
 * service actually carries. Ratios (Reg->FTD) are derived from these totals in the component, never summed.
 */
export const VIDEO_STAT_DEFS: readonly VideoStatDef[] = [
  { id: 'registrations', fields: ['registrations'], kind: 'count' },
  { id: 'ftds', fields: ['ftds'], kind: 'count' },
  { id: 'deposits', fields: ['deposits'], kind: 'count' },
  { id: 'deposit_amount', fields: ['deposit_amount'], kind: 'money' },
  { id: 'ftd_value', fields: ['ftd_total_amount'], kind: 'money' },
  { id: 'ggr', fields: ['casino_ggr', 'sport_ggr'], kind: 'money' },
];

/** Sum one data_points field across several campaigns' metric objects (each value coerced with toNumber). */
export const sumMetricField = (metricsList: readonly Metrics[], field: string): number =>
  metricsList.reduce((total, m) => total + toNumber(m[field]), 0);

/** Sum a KPI's fields across all the ad's campaigns, scaling money to major units; counts pass through. */
export const videoStatValue = (metricsList: readonly Metrics[], def: VideoStatDef): number => {
  const raw = def.fields.reduce((sum, field) => sum + sumMetricField(metricsList, field), 0);
  return def.kind === 'money' ? raw / AMOUNT_SCALE : raw;
};

/** A ratio (0..1) or undefined when the denominator is 0/missing, so the UI shows a dash not a div-by-zero. */
export const statRatio = (numerator: number, denominator: number): number | undefined =>
  Number.isFinite(numerator) && Number.isFinite(denominator) && denominator !== 0 ? numerator / denominator : undefined;
